/**
 * PipelineKanbanBoard — Full process-stage kanban (RM / P1–P11 / QC-1 / QC-2 /
 * QC-3 / Dispatch) with card tiles, Rework, and Advance actions.
 *
 * Self-contained: no outer page chrome, no AppShell assumptions. Designed to be
 * embedded at the bottom of ProductionDashboard (v3) as the "Pipeline" section.
 *
 * Extracted from the old ManufacturingDashboardPage (Apr 24, 2026) as part of
 * the v3 dashboard consolidation — replaces the condensed LegacyPipelineView.
 *
 * Backend endpoints (unchanged):
 *   GET   /manufacturing-flow/dashboard              — stage-grouped orders + stats + config
 *   PATCH /manufacturing-flow/orders/:id/advance     — move to next stage
 *   PATCH /manufacturing-flow/orders/:id/rework      — return to prior stage with reason
 *
 * Styling: uses warm-ivory / Fraunces / Outfit tokens (var(--ivory-*),
 *   var(--ink-*), var(--saffron-*), var(--forest-*), var(--line), .card,
 *   .chip, .btn) — matches the rest of ProductionDashboard.
 *
 * No tenant-slug hardcoding (D-09). Stage list comes from the backend.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, Clock, Eye, EyeOff, Loader2, Plus, RefreshCw, X } from 'lucide-react';
import { api } from '@/utils/api';

// ─── Types ──────────────────────────────────────────────────────────────

interface ManufacturingOrder {
  id: string;
  orderNumber: string;
  productName: string;
  triggerSource: 'stock_order' | 'sales_order' | 'production_plan';
  targetQty: number;
  isSWH: boolean;
  currentProcess: number;
  status: string;
  priority: string;
  updatedAt: string;
  createdAt: string;
}

interface StageInfo {
  num: number;
  label: string;
  key: string;
  swhOnly?: boolean;
}

interface DashboardData {
  orders: ManufacturingOrder[];
  grouped: Record<string, ManufacturingOrder[]>;
  stats: {
    totalActive: number;
    inProgress: number;
    atQC: number;
    completedToday: number;
  };
  stages?: StageInfo[];
  config?: {
    pipelineMode?: 'full' | 'simplified';
  };
}

// ─── Stage metadata ─────────────────────────────────────────────────────

const STAGE_LABELS: Record<number, string> = {
  0:  'RM Inspection',
  1:  'P1: Sheet Cutting',
  2:  'P2: CNC Punching',
  3:  'P3: SWH Frame Cut',
  4:  'P4: Inner Tank Weld',
  5:  'P5: Leakage Test',
  6:  'P6: Surface Cleaning',
  7:  'P7: Outer Shell',
  8:  'P8: Assembly',
  9:  'P9: PUF Injection',
  10: 'P10: Curing',
  11: 'P11: Packaging',
  12: 'QC-1: PUF Check',
  13: 'QC-2: Pack & Label',
  14: 'QC-3: Final Insp.',
  15: 'Dispatch',
};

const STAGE_SHORT: Record<number, string> = {
  0: 'RM', 1: 'P1', 2: 'P2', 3: 'P3', 4: 'P4', 5: 'P5', 6: 'P6', 7: 'P7',
  8: 'P8', 9: 'P9', 10: 'P10', 11: 'P11', 12: 'QC1', 13: 'QC2', 14: 'QC3', 15: 'DSP',
};

// Stages 0, 5, 12, 13, 14 are QC checkpoints — rendered with saffron accents.
// Everything else is a manufacturing stage — rendered with forest accents.
const QC_STAGES = new Set([0, 5, 12, 13, 14]);

const TRIGGER_CHIP: Record<string, 'amber' | 'blue' | 'green'> = {
  stock_order: 'amber',
  sales_order: 'blue',
  production_plan: 'green',
};

const TRIGGER_LABEL: Record<string, string> = {
  stock_order: 'Stock',
  sales_order: 'SO',
  production_plan: 'Plan',
};

const PRIORITY_DOT: Record<string, string> = {
  low:      'var(--ink-4, #9CA3AF)',
  medium:   '#3B82F6',
  high:     'var(--saffron, #F59E0B)',
  critical: 'var(--danger, #EF4444)',
};

// ─── Helpers ────────────────────────────────────────────────────────────

function timeInStage(updatedAt: string): string {
  const diff = Date.now() - new Date(updatedAt).getTime();
  const h = Math.floor(diff / 3_600_000);
  const m = Math.floor((diff % 3_600_000) / 60_000);
  if (h > 48) return `${Math.floor(h / 24)}d`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

// ─── Rework Dialog ──────────────────────────────────────────────────────

function ReworkDialog({
  order,
  onClose,
  onSuccess,
  onError,
}: {
  order: ManufacturingOrder;
  onClose: () => void;
  onSuccess: () => void;
  onError?: (msg: string) => void;
}) {
  const [targetProcess, setTargetProcess] = useState(0);
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const backdropRef = useRef<HTMLDivElement>(null);

  const handleSubmit = async () => {
    if (!reason.trim()) return;
    setSubmitting(true);
    try {
      await api.patch(`/manufacturing-flow/orders/${order.id}/rework`, {
        toProcess: targetProcess,
        reason: reason.trim(),
      });
      onSuccess();
    } catch (err: any) {
      const msg = err.response?.data?.error || 'Rework failed';
      onError?.(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      ref={backdropRef}
      onClick={(e) => e.target === backdropRef.current && onClose()}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 50,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'color-mix(in srgb, var(--ink-0) 40%, transparent)',
      }}
    >
      <div
        className="card"
        style={{ width: 340, padding: 20 }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <h3 style={{ fontFamily: 'var(--ff-serif)', fontSize: 15, fontWeight: 500, color: 'var(--ink-0)', margin: 0 }}>
            Send for Rework
          </h3>
          <button
            onClick={onClose}
            style={{
              padding: 4,
              borderRadius: 4,
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              color: 'var(--ink-3)',
            }}
          >
            <X size={14} />
          </button>
        </div>
        <p style={{ fontSize: 12, color: 'var(--ink-3)', marginBottom: 12 }}>
          {order.orderNumber} — {order.productName}
        </p>
        <label style={{ display: 'block', fontSize: 11, fontWeight: 500, color: 'var(--ink-2)', marginBottom: 4 }}>
          Target Stage
        </label>
        <select
          value={targetProcess}
          onChange={(e) => setTargetProcess(Number(e.target.value))}
          style={{
            width: '100%',
            marginBottom: 12,
            padding: '6px 8px',
            fontSize: 12,
            borderRadius: 6,
            border: '1px solid var(--line, var(--ivory-3))',
            background: 'var(--ivory-0)',
            color: 'var(--ink-1)',
          }}
        >
          {Array.from({ length: order.currentProcess }, (_, i) => i).map((s) => (
            <option key={s} value={s}>{STAGE_LABELS[s] ?? `Stage ${s}`}</option>
          ))}
        </select>
        <label style={{ display: 'block', fontSize: 11, fontWeight: 500, color: 'var(--ink-2)', marginBottom: 4 }}>
          Reason
        </label>
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={2}
          placeholder="Why is rework needed?"
          style={{
            width: '100%',
            marginBottom: 12,
            padding: '6px 8px',
            fontSize: 12,
            borderRadius: 6,
            border: '1px solid var(--line, var(--ivory-3))',
            background: 'var(--ivory-0)',
            color: 'var(--ink-1)',
            resize: 'none',
            fontFamily: 'inherit',
          }}
        />
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button className="btn btn-outline btn-sm" onClick={onClose} style={{ fontSize: 12, padding: '4px 12px' }}>
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting || !reason.trim()}
            style={{
              borderRadius: 6,
              border: 'none',
              background: 'var(--danger, #EF4444)',
              color: '#fff',
              fontSize: 12,
              fontWeight: 500,
              padding: '6px 12px',
              cursor: submitting || !reason.trim() ? 'not-allowed' : 'pointer',
              opacity: submitting || !reason.trim() ? 0.5 : 1,
            }}
          >
            {submitting ? 'Sending…' : 'Send for Rework'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Order Card ─────────────────────────────────────────────────────────

function OrderCard({
  order,
  onClick,
  onAdvance,
  onRework,
}: {
  order: ManufacturingOrder;
  onClick: () => void;
  onAdvance: () => void;
  onRework: () => void;
}) {
  const triggerTone = TRIGGER_CHIP[order.triggerSource] ?? '';
  const triggerLabel = TRIGGER_LABEL[order.triggerSource] ?? order.triggerSource;
  const dotColor = PRIORITY_DOT[order.priority] ?? 'var(--ink-4, #9CA3AF)';
  const isLastStage = order.currentProcess >= 15;

  return (
    <div
      onClick={onClick}
      className="card"
      style={{
        padding: 10,
        cursor: 'pointer',
        transition: 'box-shadow .15s, border-color .15s',
      }}
    >
      {/* Row 1 — order # + trigger chip */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 4, marginBottom: 6 }}>
        <span
          style={{
            fontFamily: 'monospace',
            fontSize: 11,
            fontWeight: 600,
            color: 'var(--saffron-700, var(--saffron))',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {order.orderNumber}
        </span>
        <span className={`chip${triggerTone ? ` chip-${triggerTone}` : ''}`} style={{ fontSize: 10, flexShrink: 0 }}>
          {triggerLabel}
        </span>
      </div>

      {/* Row 2 — product name */}
      <div
        style={{
          fontSize: 12,
          fontWeight: 500,
          color: 'var(--ink-1)',
          lineHeight: 1.35,
          marginBottom: 8,
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
        }}
      >
        {order.productName}
      </div>

      {/* Row 3 — priority dot / qty, SWH badge / time-in-stage */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ width: 6, height: 6, borderRadius: 3, background: dotColor, display: 'inline-block' }} />
          <span style={{ fontSize: 10, color: 'var(--ink-3)' }}>Qty: {order.targetQty}</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          {order.isSWH && (
            <span
              className="chip"
              style={{
                fontSize: 9,
                fontWeight: 600,
                background: 'color-mix(in srgb, var(--saffron) 18%, var(--ivory-0))',
                color: 'var(--saffron-700, var(--saffron))',
              }}
            >
              SWH
            </span>
          )}
          <span style={{ fontSize: 10, color: 'var(--ink-3)', display: 'inline-flex', alignItems: 'center', gap: 2 }}>
            <Clock size={10} />
            {timeInStage(order.updatedAt)}
          </span>
        </div>
      </div>

      {/* Row 4 — actions */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          paddingTop: 8,
          borderTop: '1px solid var(--line, var(--ivory-3))',
        }}
      >
        <button
          onClick={(e) => { e.stopPropagation(); onRework(); }}
          style={{
            background: 'transparent',
            border: 'none',
            padding: 0,
            fontSize: 10,
            fontWeight: 500,
            color: 'var(--danger, #EF4444)',
            cursor: 'pointer',
          }}
        >
          ↩ Rework
        </button>
        {!isLastStage && (
          <button
            onClick={(e) => { e.stopPropagation(); onAdvance(); }}
            style={{
              borderRadius: 999,
              border: 'none',
              background: 'var(--saffron, #F59E0B)',
              color: '#fff',
              fontSize: 10,
              fontWeight: 600,
              padding: '3px 10px',
              cursor: 'pointer',
            }}
          >
            Advance ›
          </button>
        )}
      </div>
    </div>
  );
}

// ─── Kanban Column ──────────────────────────────────────────────────────

function KanbanColumn({
  stageId,
  stageLabel,
  orders,
  onCardClick,
  onAdvance,
  onRework,
}: {
  stageId: number;
  stageLabel?: string;
  orders: ManufacturingOrder[];
  onCardClick: (id: string) => void;
  onAdvance: (order: ManufacturingOrder) => void;
  onRework: (order: ManufacturingOrder) => void;
}) {
  const label = stageLabel ?? STAGE_LABELS[stageId] ?? `Stage ${stageId}`;
  const isQC = QC_STAGES.has(stageId);
  const accent = isQC ? 'var(--saffron)' : 'var(--forest)';
  const headerBg = isQC
    ? 'color-mix(in srgb, var(--saffron) 14%, var(--ivory-0))'
    : 'color-mix(in srgb, var(--forest) 12%, var(--ivory-0))';
  const colBg = isQC
    ? 'color-mix(in srgb, var(--saffron) 5%, var(--ivory-0))'
    : 'color-mix(in srgb, var(--forest) 4%, var(--ivory-0))';

  const count = orders.length;
  const countTone = count === 0 ? '' : count <= 3 ? 'blue' : count <= 7 ? 'amber' : 'red';

  return (
    <div
      style={{
        flexShrink: 0,
        width: 208,
        minHeight: 300,
        borderRadius: 8,
        border: '1px solid var(--line, var(--ivory-3))',
        borderTop: `3px solid ${accent}`,
        background: colBg,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: '8px 12px',
          borderBottom: '1px solid var(--line, var(--ivory-3))',
          background: headerBg,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span
            style={{
              fontSize: 11,
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: 0.4,
              color: isQC ? 'var(--saffron-700, var(--saffron))' : 'var(--forest)',
              lineHeight: 1.2,
            }}
          >
            {label}
          </span>
          <span className={`chip${countTone ? ` chip-${countTone}` : ''}`} style={{ fontSize: 10, fontWeight: 700 }}>
            {count}
          </span>
        </div>
      </div>

      {/* Cards */}
      <div
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: 8,
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
        }}
      >
        {orders.length === 0 ? (
          <p style={{ padding: '16px 0', textAlign: 'center', fontSize: 10, color: 'var(--ink-3)', margin: 0 }}>
            No orders
          </p>
        ) : (
          orders.map((o) => (
            <OrderCard
              key={o.id}
              order={o}
              onClick={() => onCardClick(o.id)}
              onAdvance={() => onAdvance(o)}
              onRework={() => onRework(o)}
            />
          ))
        )}
      </div>
    </div>
  );
}

// ─── Collapsed column pill (shown when "Active only" hides empty stages) ─

function CollapsedColumnPill({ stageId }: { stageId: number }) {
  const short = STAGE_SHORT[stageId] ?? `S${stageId}`;
  const isQC = QC_STAGES.has(stageId);
  const accent = isQC
    ? 'color-mix(in srgb, var(--saffron) 14%, var(--ivory-0))'
    : 'color-mix(in srgb, var(--forest) 10%, var(--ivory-0))';

  return (
    <div
      title={STAGE_LABELS[stageId] ?? `Stage ${stageId}`}
      style={{
        flexShrink: 0,
        width: 32,
        minHeight: 80,
        borderRadius: 6,
        background: accent,
        border: '1px solid var(--line, var(--ivory-3))',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 4,
        opacity: 0.6,
      }}
    >
      <span
        style={{
          fontSize: 9,
          fontWeight: 700,
          color: 'var(--ink-3)',
          writingMode: 'vertical-lr',
          transform: 'rotate(180deg)',
        }}
      >
        {short}
      </span>
      <span
        style={{
          width: 16,
          height: 16,
          borderRadius: 8,
          background: 'var(--ivory-2)',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 8,
          fontWeight: 700,
          color: 'var(--ink-3)',
        }}
      >
        0
      </span>
    </div>
  );
}

// ─── Main exported component ────────────────────────────────────────────

export function PipelineKanbanBoard() {
  const navigate = useNavigate();
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reworkOrder, setReworkOrder] = useState<ManufacturingOrder | null>(null);
  const [advancingId, setAdvancingId] = useState<string | null>(null);
  const [toast, setToast] = useState<{ msg: string; type: 'error' | 'success' } | null>(null);
  const [activeOnly, setActiveOnly] = useState(true);

  const showToast = (msg: string, type: 'error' | 'success' = 'error') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4000);
  };

  const fetchDashboard = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await api.get('/manufacturing-flow/dashboard');
      setData(res.data.data ?? res.data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load pipeline');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  const handleAdvance = async (order: ManufacturingOrder) => {
    if (advancingId) return;
    setAdvancingId(order.id);
    try {
      await api.patch(`/manufacturing-flow/orders/${order.id}/advance`);
      await fetchDashboard();
    } catch (err: any) {
      showToast(err.response?.data?.error || 'Failed to advance order', 'error');
    } finally {
      setAdvancingId(null);
    }
  };

  // Loading state (first render)
  if (loading && !data) {
    return (
      <div className="card" style={{ padding: 40, textAlign: 'center' }}>
        <Loader2 size={22} style={{ animation: 'spin 1s linear infinite', color: 'var(--saffron)' }} />
        <div style={{ marginTop: 8, fontSize: 12, color: 'var(--ink-3)' }}>Loading pipeline…</div>
      </div>
    );
  }

  // Error state
  if (error && !data) {
    return (
      <div className="card" style={{ padding: 32, textAlign: 'center' }}>
        <AlertTriangle size={28} style={{ color: 'var(--danger)', marginBottom: 8 }} />
        <div style={{ fontSize: 13, color: 'var(--danger)', marginBottom: 12 }}>{error}</div>
        <button className="btn btn-outline btn-sm" onClick={fetchDashboard} style={{ fontSize: 12, padding: '4px 12px' }}>
          Retry
        </button>
      </div>
    );
  }

  const grouped = data?.grouped ?? {};
  const totalActive = data?.stats?.totalActive ?? 0;

  // Stage list — backend is source of truth. Falls back to 0–15 if absent.
  const stageList: { id: number; label?: string }[] =
    data?.stages && data.stages.length > 0
      ? data.stages.map((s) => ({ id: s.num, label: s.label }))
      : Array.from({ length: 16 }, (_, i) => ({ id: i }));

  return (
    <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
      {/* Section header */}
      <div
        style={{
          padding: '14px 16px',
          borderBottom: '1px solid var(--line, var(--ivory-3))',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          flexWrap: 'wrap',
        }}
      >
        <div className="kpi-label">Pipeline · Kanban</div>
        <span className="chip" style={{ fontSize: 11 }}>
          <span className="num">{totalActive}</span>&nbsp;active
        </span>
        {data?.config?.pipelineMode === 'simplified' && (
          <span className="chip chip-blue" style={{ fontSize: 10 }}>Simplified</span>
        )}
        <div style={{ flex: 1 }} />

        <button
          onClick={() => setActiveOnly((p) => !p)}
          className="btn btn-outline btn-sm"
          style={{
            fontSize: 11,
            padding: '4px 10px',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
            background: activeOnly ? 'color-mix(in srgb, var(--saffron) 12%, var(--ivory-0))' : undefined,
            borderColor: activeOnly ? 'var(--saffron)' : undefined,
            color: activeOnly ? 'var(--saffron-700, var(--saffron))' : undefined,
          }}
          title={activeOnly ? 'Showing active stages only' : 'Showing all stages'}
        >
          {activeOnly ? <Eye size={12} /> : <EyeOff size={12} />}
          Active only
        </button>
        <button
          onClick={fetchDashboard}
          className="btn btn-outline btn-sm"
          style={{ fontSize: 11, padding: '4px 10px', display: 'inline-flex', alignItems: 'center', gap: 4 }}
        >
          <RefreshCw size={12} />
          Refresh
        </button>
        <button
          onClick={() => navigate('/manufacturing/orders/new')}
          style={{
            borderRadius: 6,
            border: 'none',
            background: 'var(--saffron, #F59E0B)',
            color: '#fff',
            fontSize: 11,
            fontWeight: 600,
            padding: '5px 10px',
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
          }}
        >
          <Plus size={12} />
          New Order
        </button>
      </div>

      {/* Kanban scroll area */}
      <div style={{ padding: 16, overflowX: 'auto' }}>
        <div style={{ display: 'flex', gap: 8, minWidth: 'max-content' }}>
          {stageList.map((stage) => {
            const stageOrders = grouped[String(stage.id)] ?? [];
            const isEmpty = stageOrders.length === 0;

            if (activeOnly && isEmpty) {
              return <CollapsedColumnPill key={stage.id} stageId={stage.id} />;
            }

            return (
              <KanbanColumn
                key={stage.id}
                stageId={stage.id}
                stageLabel={stage.label}
                orders={stageOrders}
                onCardClick={(id) => navigate(`/manufacturing/orders/${id}`)}
                onAdvance={handleAdvance}
                onRework={(order) => setReworkOrder(order)}
              />
            );
          })}
        </div>
      </div>

      {/* Rework dialog */}
      {reworkOrder && (
        <ReworkDialog
          order={reworkOrder}
          onClose={() => setReworkOrder(null)}
          onSuccess={() => {
            setReworkOrder(null);
            fetchDashboard();
            showToast('Rework sent', 'success');
          }}
          onError={(msg) => {
            setReworkOrder(null);
            showToast(msg, 'error');
          }}
        />
      )}

      {/* Toast */}
      {toast && (
        <div
          style={{
            position: 'fixed',
            top: 16,
            right: 16,
            zIndex: 9999,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 10,
            padding: '10px 14px',
            borderRadius: 10,
            boxShadow: '0 6px 20px rgba(0,0,0,.15)',
            fontSize: 13,
            fontWeight: 500,
            background: toast.type === 'error' ? 'var(--danger, #EF4444)' : 'var(--forest)',
            color: '#fff',
          }}
        >
          <AlertTriangle size={14} />
          {toast.msg}
          <button
            onClick={() => setToast(null)}
            style={{ marginLeft: 4, background: 'transparent', border: 'none', color: '#fff', opacity: 0.8, cursor: 'pointer' }}
          >
            <X size={12} />
          </button>
        </div>
      )}
    </div>
  );
}

export default PipelineKanbanBoard;
